import React, { useState, useEffect } from 'react';
import api from '../../services/api';

const PlanSelectionRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [approvingId, setApprovingId] = useState(null);
  const [approveModal, setApproveModal] = useState({
    open: false,
    request: null,
    business_vertical: 'distribution',
  });

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const response = await api.getPlanSelectionRequests();
      if (response.success) {
        setRequests(response.data || []);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const openApproveModal = (request) => {
    setApproveModal({
      open: true,
      request,
      business_vertical: request.business_vertical || 'distribution',
    });
  };

  const closeApproveModal = () => {
    setApproveModal({ open: false, request: null, business_vertical: 'distribution' });
  };

  const handleApprove = async () => {
    const id = approveModal.request?.id;
    if (!id) return;

    try {
      setApprovingId(id);
      const response = await api.approvePlanRequest(id, {
        start_date: new Date().toISOString().split('T')[0],
        expiry_date: (() => {
          const date = new Date();
          date.setMonth(date.getMonth() + 1);
          return date.toISOString().split('T')[0];
        })(),
        business_vertical: approveModal.business_vertical,
      });

      if (response.success) {
        closeApproveModal();
        loadRequests();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Rejection reason:');
    if (!reason) return;

    try {
      const response = await api.rejectPlanRequest(id, { notes: reason });
      if (response.success) {
        loadRequests();
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-warning',
      approved: 'bg-success',
      rejected: 'bg-danger',
      completed: 'bg-info',
    };
    return badges[status] || 'bg-secondary';
  };

  if (loading) {
    return <div className="text-center p-4">Loading...</div>;
  }

  return (
    <>
      {error && (
        <div className="alert alert-danger alert-dismissible">
          <button type="button" className="close" onClick={() => setError('')}>&times;</button>
          {error}
        </div>
      )}

      <div className="row">
        <div className="col-md-12">
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Plan Selection Requests</h3>
            </div>
            <div className="card-body">
              <table className="table table-bordered table-hover">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Company</th>
                    <th>Tier</th>
                    <th>Vertical</th>
                    <th>Contact</th>
                    <th>Billing</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length > 0 ? (
                    requests.map(request => (
                      <tr key={request.id}>
                        <td>{request.id}</td>
                        <td>{request.company_name}</td>
                        <td>
                          <span className="badge bg-info">{request.tier_name}</span>
                        </td>
                        <td>
                          <div style={{ fontSize: '11px' }}>
                            <span className="badge bg-secondary">
                              {request.business_vertical || 'distribution'}
                            </span>
                            {request.approved_vertical && request.approved_vertical !== request.business_vertical ? (
                              <div className="mt-1">
                                <span className="badge bg-success">
                                  approved: {request.approved_vertical}
                                </span>
                              </div>
                            ) : null}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: '10px' }}>
                            <div>{request.contact_name || 'N/A'}</div>
                            <div>{request.contact_email}</div>
                            {request.contact_phone && <div>{request.contact_phone}</div>}
                          </div>
                        </td>
                        <td>{request.billing_period}</td>
                        <td>
                          <span className={`badge ${getStatusBadge(request.status)}`}>
                            {request.status}
                          </span>
                        </td>
                        <td>{new Date(request.created_at).toLocaleDateString()}</td>
                        <td>
                          {request.status === 'pending' && (
                            <>
                              <button
                                className="btn btn-sm btn-success mr-1"
                                onClick={() => openApproveModal(request)}
                                disabled={approvingId === request.id}
                              >
                                <i className="fas fa-check"></i> Approve
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleReject(request.id)}
                              >
                                <i className="fas fa-times"></i> Reject
                              </button>
                            </>
                          )}
                          {request.license_key && (
                            <div style={{ fontSize: '10px' }}>
                              <code>{request.license_key}</code>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center">No requests found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {approveModal.open && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.45)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Approve Request #{approveModal.request?.id}</h5>
                <button type="button" className="close" onClick={closeApproveModal}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="mb-2">
                  Requested vertical: <strong>{approveModal.request?.business_vertical || 'distribution'}</strong>
                </p>
                <label className="mb-1">Approved vertical</label>
                <select
                  className="form-control"
                  value={approveModal.business_vertical}
                  onChange={(e) =>
                    setApproveModal((prev) => ({ ...prev, business_vertical: e.target.value }))
                  }
                >
                  <option value="distribution">Distribution</option>
                  <option value="retail">Retail</option>
                  <option value="manufacturing">Manufacturing</option>
                  <option value="hospitality">Hospitality</option>
                  <option value="port">Port</option>
                </select>
                <small className="text-muted d-block mt-2">
                  This approved vertical will be locked in the tenant license after approval.
                </small>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeApproveModal}>Cancel</button>
                <button
                  className="btn btn-success"
                  onClick={handleApprove}
                  disabled={approvingId === approveModal.request?.id}
                >
                  {approvingId === approveModal.request?.id ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PlanSelectionRequests;

